import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, X, ArrowLeft, HelpCircle, Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../Button';
import { Input } from '../Input';
import { useAuth } from '../../contexts/AuthContext';
import { useUnsavedChangesGuard } from '../../contexts/UnsavedChangesContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qrApi } from '../../api/services/qr.service';
import { QRCodeDetailResponse, QRCodeUpdate, RoutingRulesConfig, DEFAULT_QR_STYLE } from '../../types/qr.types';
import { qrFormSchema, QRFormData } from '../../utils/validation';
import { RuleBuilder } from './rules';
import { ImagePickerModal } from './ImagePickerModal';
import type { ImageItem } from '../../types/image.types';
import { QRCodePreview } from './QRCodePreview';
import { getQrTargetUrl, DOT_STYLE_OPTIONS, CORNER_SQUARE_STYLE_OPTIONS, CORNER_DOT_STYLE_OPTIONS, randomQrAppearance } from '../../utils/qr';
import { useImageDataUrl } from '../../utils/imageDataUrl';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

const defaultRoutingRules: RoutingRulesConfig = { version: 1, rules: [] };

export const EDITOR_SECTIONS = [
  { id: 'basic-info', label: 'Basic Info' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'smart-routing', label: 'Smart Routing' },
  { id: 'security', label: 'Security' },
  { id: 'utm', label: 'UTM Parameters' },
  { id: 'advanced', label: 'Advanced' },
] as const;

export type EditorSectionId = typeof EDITOR_SECTIONS[number]['id'];

interface QRCodeEditFormProps {
  initialData: QRCodeDetailResponse;
  onBack: () => void;
  onSuccess?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  /** Section to scroll to on mount, e.g. from the create modal's "Create & Customize" action. */
  initialSection?: EditorSectionId;
  /** Demo mode: every field stays fully interactive, but submit is a no-op — used by the sample QR code's edit page, which has no real backend row to persist to. */
  readOnly?: boolean;
}

function normalizeUrl(url: string): string {
  if (!url) return url;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

// scrollIntoView({ block: 'start' }) can only put an element flush with the viewport
// top if the scroll container has enough room below it to actually scroll that far —
// a section near the end of a page shorter than the viewport (e.g. "Advanced", the
// last one) otherwise gets clamped at max-scroll, landing wherever that happens to
// fall short of "flush at top" rather than at the target. Compute and clamp the
// scroll position explicitly instead: if the section can't reach the top, scroll as
// far as the page actually allows, so its content is still on screen with no
// artificial trailing whitespace needed to compensate.
function scrollElementIntoViewClamped(el: HTMLElement) {
  const container = document.getElementById('dashboard-content-area');
  if (!container) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  const scrollMarginTop = parseFloat(getComputedStyle(el).scrollMarginTop || '0');
  const targetTop = el.offsetTop - scrollMarginTop;
  const maxScrollTop = container.scrollHeight - container.clientHeight;
  container.scrollTo({ top: Math.min(targetTop, maxScrollTop), behavior: 'smooth' });
}

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
// The native <input type="color"> swatch needs a strictly valid 7-char hex at all
// times — falls back to the last valid color while the paired text field is
// mid-edit (e.g. "#12"), instead of flashing black.
function safeHexForPicker(value: string, fallback: string): string {
  return HEX_COLOR_RE.test(value) ? value : fallback;
}

// Preview backdrop for a transparent background, so "transparent" actually looks
// different from white in the live preview instead of the two being indistinguishable.
const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  backgroundColor: '#fff',
};

function SectionInfoButton({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="w-6 h-6 rounded-full bg-[--clinqr-navy]/10 hover:bg-gray-300 flex items-center justify-center transition-colors"
    >
      <HelpCircle className="w-4 h-4 text-[--clinqr-navy]" />
    </button>
  );
}

/** Small "?" affordance next to a field label — a one-line hover/focus explainer,
 * lighter-weight than SectionInfoButton's modal (used for whole-section explanations). */
function FieldInfoTooltip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          tabIndex={0}
          className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[--clinqr-text-muted] hover:text-[--clinqr-text-secondary] transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
          <span className="sr-only">More info</span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[220px] text-center">{text}</TooltipContent>
    </Tooltip>
  );
}

function FieldLabel({ htmlFor, children, info }: { htmlFor: string; children: React.ReactNode; info: string }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-[--clinqr-text-primary]">
        {children}
      </label>
      <FieldInfoTooltip text={info} />
    </div>
  );
}

export function QRCodeEditForm({ initialData, onBack, onSuccess, onDirtyChange, initialSection, readOnly = false }: QRCodeEditFormProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Guards in-app navigation triggered from inside this page (the Back button here,
  // DashboardLayout's own sidebar/logo) — see UnsavedChangesContext for why this can't
  // live in QRCodeEditPage itself (it's rendered outside DashboardLayout's provider).
  const { setDirty, guardNavigation } = useUnsavedChangesGuard();

  // The header/nav bar portals into DashboardLayout's non-scrolling subheader slot so it
  // sits flush under the top bar with no padding to cancel out (see DashboardLayout.tsx).
  const [subheaderRoot, setSubheaderRoot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setSubheaderRoot(document.getElementById('dashboard-subheader-root'));
  }, []);

  // Large-title-collapses-to-compact-bar-title effect: 0 at the top of the page (large
  // title visible, compact bar title hidden), 1 once scrolled past ~72px (reverse).
  // Driven directly off DashboardLayout's scroll container rather than a CSS-only
  // approach, since the compact title lives in the portaled bar outside this flow.
  const [titleScrollProgress, setTitleScrollProgress] = useState(0);
  useEffect(() => {
    const el = document.getElementById('dashboard-content-area');
    if (!el) return;
    const COLLAPSE_DISTANCE = 72;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setTitleScrollProgress(Math.min(el.scrollTop / COLLAPSE_DISTANCE, 1));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const [selectedTags, setSelectedTags] = useState<number[]>(initialData.tags?.map((t) => t.id) || []);
  const [routingRules, setRoutingRules] = useState<RoutingRulesConfig>(
    (initialData.routing_rules as RoutingRulesConfig) || defaultRoutingRules
  );
  const [showRulesInfoModal, setShowRulesInfoModal] = useState(false);
  const [showUtmInfoModal, setShowUtmInfoModal] = useState(false);
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showFallbackImagePicker, setShowFallbackImagePicker] = useState(false);
  const [fallbackImage, setFallbackImage] = useState<{ id: number; url: string; filename?: string } | null>(
    initialData.fallback_image_id
      ? { id: initialData.fallback_image_id, url: initialData.fallback_image_url || '' }
      : null
  );
  const savedFallbackUrlRef = useRef<string>(
    !initialData.fallback_image_id ? (initialData.fallback_url || '') : ''
  );
  // Resolves logo_image_id to an actual URL for preview/thumbnail rendering, mirroring
  // fallbackImage above. Without this, a My-Images logo never renders anywhere (preview,
  // downloaded QR, or this thumbnail) since nothing else in the form maps the id to a URL.
  const [logoImage, setLogoImage] = useState<{ id: number; url: string; filename?: string } | null>(
    initialData.logo_image_id
      ? { id: initialData.logo_image_id, url: initialData.logo_image_url || '' }
      : null
  );
  // Resolved as data: URIs (not the raw URLs) for these thumbnails specifically because
  // QRCodePreview's live embed of the *same* URL needs a crossOrigin fetch -- a plain
  // thumbnail <img> and a crossOrigin fetch racing for the identical URL can trip a
  // Chromium cache-write race that fails one of them. See utils/imageDataUrl.ts.
  const fallbackImageDataUrl = useImageDataUrl(fallbackImage?.url);
  const logoImageDataUrl = useImageDataUrl(logoImage?.url);

  // Password: inline change/remove, no dialogs — see docs/design/qr-form-redesign.md §2c.
  // Not re-derived after save: a successful save navigates away (see QRCodeEditPage's onSuccess).
  const passwordProtected = initialData.password_protected ?? false;
  const [showPasswordField, setShowPasswordField] = useState(!initialData.password_protected);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [pendingPasswordRemoval, setPendingPasswordRemoval] = useState(false);

  const [activeSection, setActiveSection] = useState<EditorSectionId>(initialSection || 'basic-info');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const didScrollToInitialSection = useRef(false);

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: qrApi.getTags,
  });

  const form = useForm<QRFormData>({
    resolver: zodResolver(qrFormSchema),
    defaultValues: {
      title: initialData.title,
      description: initialData.description || '',
      fallback_url: initialData.fallback_url,
      is_active: initialData.is_active,
      background_color: initialData.background_color || '#FFFFFF',
      foreground_color: initialData.foreground_color || '#000000',
      qr_style: { ...DEFAULT_QR_STYLE, ...initialData.qr_style },
      custom_logo_url: initialData.custom_logo_url || '',
      logo_image_id: initialData.logo_image_id ?? null,
      fallback_image_id: initialData.fallback_image_id ?? null,
      tag_ids: initialData.tags?.map((t) => t.id) || [],
      password: '',
      expires_at: initialData.expires_at ? new Date(initialData.expires_at).toISOString().slice(0, 16) : '',
      max_scans: initialData.max_scans || undefined,
      utm_parameters: {
        utm_source: initialData.utm_parameters?.utm_source || '',
        utm_medium: initialData.utm_parameters?.utm_medium || '',
        utm_campaign: initialData.utm_parameters?.utm_campaign || '',
        utm_term: initialData.utm_parameters?.utm_term || '',
        utm_content: initialData.utm_parameters?.utm_content || '',
      },
    },
  });

  const { isDirty } = form.formState;
  useEffect(() => {
    // In demo mode there's nothing to save, so never arm the "leave without
    // saving?" guard — editing is meant to be freely explorable/discardable.
    if (readOnly) return;
    const dirty = isDirty || pendingPasswordRemoval || !!form.watch('password');
    onDirtyChange?.(dirty);
    setDirty(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, pendingPasswordRemoval, readOnly]);

  const watchedFallbackUrl = form.watch('fallback_url');
  const watchedBgColor = form.watch('background_color') || '#FFFFFF';
  const isTransparentBg = watchedBgColor === 'transparent';
  // Remembers the last solid color so unchecking "Transparent background" restores it
  // instead of resetting to plain white.
  const lastSolidBgColorRef = useRef<string>(
    initialData.background_color && initialData.background_color !== 'transparent'
      ? initialData.background_color
      : '#FFFFFF'
  );
  if (!isTransparentBg && HEX_COLOR_RE.test(watchedBgColor)) {
    lastSolidBgColorRef.current = watchedBgColor;
  }
  const watchedFgColor = form.watch('foreground_color') || '#000000';
  const watchedLogoImageId = form.watch('logo_image_id');
  const watchedLogoUrl = watchedLogoImageId ? logoImage?.url : form.watch('custom_logo_url');
  const watchedQrStyle = form.watch('qr_style') || {};
  const watchedCustomDomain = form.watch('custom_domain');
  const watchedIsActive = form.watch('is_active');

  const previewValue = initialData.short_code
    ? getQrTargetUrl({ short_code: initialData.short_code, custom_domain: watchedCustomDomain || initialData.custom_domain })
    : '';

  // Scroll to the requested section once, after first render.
  useEffect(() => {
    if (didScrollToInitialSection.current || !initialSection) return;
    const el = sectionRefs.current[initialSection];
    if (el) {
      scrollElementIntoViewClamped(el);
      didScrollToInitialSection.current = true;
    }
  }, [initialSection]);

  // Scrollspy: highlight whichever section is nearest the top of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const topMost = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
          setActiveSection(topMost.target.id as EditorSectionId);
        }
      },
      // No top offset needed: the header/nav bar is portaled outside the scroll
      // container now (see subheaderRoot), so it never overlaps section content.
      { rootMargin: '0px 0px -70% 0px', threshold: 0 }
    );
    EDITOR_SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((id: EditorSectionId) => {
    const el = sectionRefs.current[id];
    if (el) scrollElementIntoViewClamped(el);
  }, []);

  const selectFallbackImage = (image: ImageItem) => {
    if (!fallbackImage) {
      savedFallbackUrlRef.current = form.getValues('fallback_url') || '';
    }
    setFallbackImage({ id: image.id, url: image.url, filename: image.filename });
    form.setValue('fallback_image_id', image.id, { shouldDirty: true });
    form.setValue('fallback_url', image.url, { shouldValidate: true, shouldDirty: true });
    setShowFallbackImagePicker(false);
  };

  const removeFallbackImage = () => {
    setFallbackImage(null);
    form.setValue('fallback_image_id', null, { shouldDirty: true });
    form.setValue('fallback_url', savedFallbackUrlRef.current, { shouldValidate: true, shouldDirty: true });
  };

  const updateMutation = useMutation({
    mutationFn: (data: QRCodeUpdate) => qrApi.updateQRCode(initialData.id, data),
    onSuccess: () => {
      toast.success('QR code updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      queryClient.invalidateQueries({ queryKey: ['usage-stats'] });
      queryClient.invalidateQueries({ queryKey: ['qr-code', String(initialData.id)] });
      queryClient.invalidateQueries({ queryKey: ['qr-code', initialData.short_code] });
      setPendingPasswordRemoval(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to update QR code');
    },
  });

  const onSubmit = (data: QRFormData) => {
    if (readOnly) {
      toast('This is a demo QR code — changes here aren\'t saved.');
      return;
    }

    const cleanedData: any = { ...data };
    if (cleanedData.custom_logo_url === '') delete cleanedData.custom_logo_url;
    if (cleanedData.description === '') delete cleanedData.description;
    if (cleanedData.custom_domain === '') delete cleanedData.custom_domain;
    if (cleanedData.max_scans === null || cleanedData.max_scans === undefined || (typeof cleanedData.max_scans === 'number' && isNaN(cleanedData.max_scans))) {
      delete cleanedData.max_scans;
    }
    if (cleanedData.expires_at === '') delete cleanedData.expires_at;

    if (pendingPasswordRemoval) {
      cleanedData.password = '';
    } else if (!cleanedData.password || cleanedData.password.trim() === '') {
      delete cleanedData.password;
    }

    const utm = cleanedData.utm_parameters;
    const hasAnyUtmValue = utm && Object.values(utm).some((v) => typeof v === 'string' && v.trim() !== '');
    if (!hasAnyUtmValue) cleanedData.utm_parameters = null;

    const submitData: any = { ...cleanedData, tag_ids: selectedTags };

    if (routingRules.rules.length > 0) {
      submitData.routing_rules = {
        ...routingRules,
        rules: routingRules.rules.map((rule) => ({
          ...rule,
          action: rule.action.type === 'redirect'
            ? { ...rule.action, url: normalizeUrl(rule.action.url) }
            : rule.action.type === 'randomize'
            ? { ...rule.action, urls: rule.action.urls.map((u) => ({ ...u, url: normalizeUrl(u.url) })) }
            : rule.action,
        })),
      };
    } else {
      submitData.routing_rules = null;
    }

    updateMutation.mutate(submitData as QRCodeUpdate);
  };

  const onError = (errors: any) => {
    console.error('Form validation errors:', errors);
    toast.error('Please fix the form errors before submitting');
  };

  const handleTagToggle = (tagId: number) => {
    setSelectedTags((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const setSection = (id: EditorSectionId) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  // The bar itself shrinks on scroll — tall with a large title at the top of the page,
  // thin with a compact title once scrolled — rather than a separate title elsewhere.
  const headerBar = (
    <div
      className="bg-[--clinqr-bg-subtle] border-b border-[--clinqr-border]/10 px-3 sm:px-4 lg:px-8"
      style={{
        paddingTop: 10 + (1 - titleScrollProgress) * 22,
        paddingBottom: 6 + (1 - titleScrollProgress) * 12,
      }}
    >
      <div className="max-w-4xl mx-auto flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => guardNavigation(onBack)}
          className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary] hover:bg-white transition-colors"
          title="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <p
          className="flex-1 min-w-0 font-semibold text-[--clinqr-text-primary] truncate"
          style={{ fontSize: 15 + (1 - titleScrollProgress) * 15 }}
        >
          {initialData.title || initialData.short_code}
        </p>
        {/* form="qr-edit-form": this button is portaled outside the <form> DOM subtree
            (see subheaderRoot below), so native submit association needs the form
            attribute — a plain descendant type="submit" wouldn't reach it. */}
        <Button
          type="submit"
          form="qr-edit-form"
          variant="primary"
          size="sm"
          disabled={updateMutation.isPending || readOnly}
          title={readOnly ? 'This is a demo QR code — changes here aren\'t saved.' : undefined}
          className="!bg-[--clinqr-lime] !text-[--clinqr-navy] hover:brightness-95 border border-[--clinqr-navy]/20 flex-shrink-0 !px-3"
        >
          <span className="sm:hidden">{updateMutation.isPending ? 'Saving...' : readOnly ? "Can't Save" : 'Save'}</span>
          <span className="hidden sm:inline">{updateMutation.isPending ? 'Saving...' : readOnly ? "Demo — Can't Save" : 'Save Changes'}</span>
        </Button>
      </div>
      <nav
        className="max-w-4xl mx-auto flex items-center gap-1 overflow-x-auto mt-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Edit sections"
      >
        {EDITOR_SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => scrollToSection(section.id)}
            aria-current={activeSection === section.id ? 'true' : undefined}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
              activeSection === section.id
                ? 'bg-[--clinqr-lime] text-[--clinqr-navy]'
                : 'text-[--clinqr-text-secondary] hover:bg-white'
            }`}
          >
            {section.label}
          </button>
        ))}
      </nav>
    </div>
  );

  return (
    <form id="qr-edit-form" onSubmit={form.handleSubmit(onSubmit, onError)}>
      {subheaderRoot && createPortal(headerBar, subheaderRoot)}

      <div className="max-w-4xl mx-auto space-y-10">
        {/* ============================== Basic Info ============================== */}
        <section id="basic-info" ref={setSection('basic-info')} className="space-y-4 scroll-mt-4">
          <h2 className="text-lg font-semibold text-[--clinqr-text-primary]">Basic Info</h2>

          <Input label="Name" placeholder="Untitled QR code" {...form.register('title')} error={form.formState.errors.title?.message} />

          <div>
            <FieldLabel htmlFor="fallback_url" info="The destination scanners land on. You can use a link or attach an uploaded image instead.">
              Where should this QR code go?
            </FieldLabel>
            {fallbackImage ? (
              <div className="flex items-center gap-2">
                <input
                  key="destination-image"
                  readOnly
                  disabled
                  value={fallbackImage.filename || 'Uploaded image'}
                  className="flex-1 h-12 px-4 rounded-lg border-2 border-[--clinqr-border]
                    bg-[--clinqr-bg-subtle] text-[--clinqr-text-primary] opacity-80 cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => setShowFallbackImagePicker(true)}
                  className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden border-2 border-[--clinqr-border] hover:border-[--clinqr-primary] transition-colors"
                  title="Change image"
                >
                  {fallbackImageDataUrl && (
                    <img src={fallbackImageDataUrl} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={removeFallbackImage}
                  className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg border-2 border-[--clinqr-border] text-[--clinqr-text-secondary] hover:text-red-600 hover:border-red-300 transition-colors"
                  title="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  key="destination-url"
                  id="fallback_url"
                  placeholder="Type a link or add an image →"
                  {...form.register('fallback_url')}
                  className="flex-1 min-w-0 h-12 px-4 rounded-lg border-2 border-[--clinqr-border]
                    bg-[--clinqr-bg-subtle] text-[--clinqr-text-primary]
                    focus:outline-none focus:border-[--clinqr-primary] focus:ring-2 focus:ring-[--clinqr-primary]/20"
                />
                <button
                  type="button"
                  onClick={() => setShowFallbackImagePicker(true)}
                  title="Add image"
                  className="h-12 flex-shrink-0 flex items-center gap-2 px-4 rounded-lg border-2 border-[--clinqr-navy]/20 bg-[--clinqr-lime] text-[--clinqr-navy] hover:text-black hover:border-black transition-colors"
                >
                  <ImageIcon className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">Add Image</span>
                </button>
              </div>
            )}
            {!fallbackImage && form.formState.errors.fallback_url?.message && (
              <p className="text-[--clinqr-error] text-sm mt-1.5">{form.formState.errors.fallback_url.message}</p>
            )}
          </div>

          <div>
            <FieldLabel htmlFor="description" info="Internal notes about this QR code — not shown to anyone who scans it.">
              Description
            </FieldLabel>
            <textarea
              id="description"
              {...form.register('description')}
              placeholder="Brief description of this QR code"
              className="w-full h-20 px-4 py-3 rounded-lg border-2 border-[--clinqr-border] bg-[--clinqr-bg-subtle]
                focus:outline-none focus:border-[--clinqr-primary] focus:ring-2 focus:ring-[--clinqr-primary]/20
                resize-none text-[--clinqr-text-primary] placeholder:text-[--clinqr-text-muted]"
            />
          </div>

          {tags.length > 0 && (
            <div>
              <span className="block text-sm font-medium text-[--clinqr-text-primary] mb-2">Tags</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Tags">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    aria-pressed={selectedTags.includes(tag.id)}
                    onClick={() => handleTagToggle(tag.id)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedTags.includes(tag.id)
                        ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border-2 border-[--clinqr-navy]'
                        : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border-2 border-[--clinqr-border]/10 hover:bg-gray-200'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ============================== Appearance ============================== */}
        <section id="appearance" ref={setSection('appearance')} className="space-y-4 scroll-mt-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-[--clinqr-text-primary]">Appearance</h2>
            <button
              type="button"
              onClick={() => {
                const random = randomQrAppearance();
                form.setValue('background_color', random.background_color, { shouldDirty: true });
                form.setValue('foreground_color', random.foreground_color, { shouldDirty: true });
                form.setValue('qr_style.dots_type', random.dots_type, { shouldDirty: true });
                form.setValue('qr_style.corners_square_type', random.corners_square_type, { shouldDirty: true });
                form.setValue('qr_style.corners_dot_type', random.corners_dot_type, { shouldDirty: true });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border-2 border-[--clinqr-border]/10 hover:bg-gray-200 transition-colors"
            >
              Randomize
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-shrink-0 flex flex-col items-center gap-2">
              <div
                className="w-40 h-40 rounded-lg border border-[--clinqr-border]/20 flex items-center justify-center p-2 shadow-sm"
                style={isTransparentBg ? CHECKERBOARD_STYLE : { backgroundColor: 'white' }}
              >
                <QRCodePreview
                  value={previewValue}
                  backgroundColor={isTransparentBg ? 'transparent' : safeHexForPicker(watchedBgColor, '#FFFFFF')}
                  foregroundColor={safeHexForPicker(watchedFgColor, '#000000')}
                  logoUrl={watchedLogoUrl || undefined}
                  dotsType={watchedQrStyle.dots_type}
                  cornersSquareType={watchedQrStyle.corners_square_type}
                  cornersDotType={watchedQrStyle.corners_dot_type}
                  className="w-full h-full"
                />
              </div>
              <span className="text-[10px] text-[--clinqr-text-muted] uppercase tracking-wider font-semibold">Live Preview</span>
            </div>

            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="background_color_hex" className="block text-xs text-[--clinqr-text-secondary]">Background</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label="Background color picker"
                      disabled={isTransparentBg}
                      value={safeHexForPicker(watchedBgColor, '#FFFFFF')}
                      onChange={(e) => form.setValue('background_color', e.target.value.toUpperCase(), { shouldDirty: true, shouldValidate: true })}
                      className="w-10 h-10 flex-shrink-0 rounded border-2 border-[--clinqr-border]/10 cursor-pointer p-0.5 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                    <input
                      id="background_color_hex"
                      type="text"
                      disabled={isTransparentBg}
                      value={isTransparentBg ? 'transparent' : watchedBgColor}
                      onChange={(e) => form.setValue('background_color', e.target.value, { shouldDirty: true, shouldValidate: true })}
                      onBlur={(e) => {
                        const v = e.target.value.trim().toUpperCase();
                        form.setValue('background_color', v && !v.startsWith('#') ? `#${v}` : v, { shouldDirty: true, shouldValidate: true });
                      }}
                      placeholder="#FFFFFF"
                      className="w-24 h-9 px-2 rounded border-2 border-[--clinqr-border]/20 bg-white text-sm text-[--clinqr-text-secondary] font-mono focus:outline-none focus:border-[--clinqr-primary] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[--clinqr-bg-subtle]"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-[--clinqr-text-secondary] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isTransparentBg}
                      onChange={(e) => {
                        form.setValue(
                          'background_color',
                          e.target.checked ? 'transparent' : lastSolidBgColorRef.current,
                          { shouldDirty: true, shouldValidate: true }
                        );
                      }}
                      className="w-3.5 h-3.5 rounded border-[--clinqr-border] cursor-pointer"
                    />
                    Transparent background
                  </label>
                </div>
                <div className="space-y-2">
                  <label htmlFor="foreground_color_hex" className="block text-xs text-[--clinqr-text-secondary]">Foreground</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label="Foreground color picker"
                      value={safeHexForPicker(watchedFgColor, '#000000')}
                      onChange={(e) => form.setValue('foreground_color', e.target.value.toUpperCase(), { shouldDirty: true, shouldValidate: true })}
                      className="w-10 h-10 flex-shrink-0 rounded border-2 border-[--clinqr-border]/10 cursor-pointer p-0.5 bg-white"
                    />
                    <input
                      id="foreground_color_hex"
                      type="text"
                      value={watchedFgColor}
                      onChange={(e) => form.setValue('foreground_color', e.target.value, { shouldDirty: true, shouldValidate: true })}
                      onBlur={(e) => {
                        const v = e.target.value.trim().toUpperCase();
                        form.setValue('foreground_color', v && !v.startsWith('#') ? `#${v}` : v, { shouldDirty: true, shouldValidate: true });
                      }}
                      placeholder="#000000"
                      className="w-24 h-9 px-2 rounded border-2 border-[--clinqr-border]/20 bg-white text-sm text-[--clinqr-text-secondary] font-mono focus:outline-none focus:border-[--clinqr-primary]"
                    />
                  </div>
                </div>
              </div>
              {form.formState.errors.background_color && (
                <p className="text-red-500 text-xs">{form.formState.errors.background_color.message}</p>
              )}
              {form.formState.errors.foreground_color && (
                <p className="text-red-500 text-xs">{form.formState.errors.foreground_color.message}</p>
              )}

              {form.watch('logo_image_id') ? (
                <div className="space-y-2">
                  <span className="block text-sm font-medium text-[--clinqr-text-primary]">Logo</span>
                  <div className="flex items-center gap-3">
                    {logoImageDataUrl ? (
                      <img
                        src={logoImageDataUrl}
                        alt=""
                        className="w-10 h-10 rounded border-2 border-[--clinqr-border] object-cover flex-shrink-0"
                      />
                    ) : null}
                    <span className="text-sm text-[--clinqr-text-secondary] truncate">
                      {logoImage?.filename || `Image #${form.watch('logo_image_id')}`}
                    </span>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setShowLogoPicker(true)}>Change</Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        form.setValue('logo_image_id', null, { shouldDirty: true });
                        setLogoImage(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    label="Logo URL"
                    placeholder="https://example.com/logo.png"
                    {...form.register('custom_logo_url')}
                    error={form.formState.errors.custom_logo_url?.message}
                  />
                  <button type="button" onClick={() => setShowLogoPicker(true)} className="text-sm text-[--clinqr-primary] hover:underline">
                    Or choose from My Images
                  </button>
                </div>
              )}

              <div className="space-y-2">
                <span className="block text-xs text-[--clinqr-text-secondary]">Dot Style</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Dot style">
                  {DOT_STYLE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      aria-pressed={(watchedQrStyle.dots_type || 'square') === opt.value}
                      onClick={() => form.setValue('qr_style.dots_type', opt.value, { shouldDirty: true })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        (watchedQrStyle.dots_type || 'square') === opt.value
                          ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border-2 border-[--clinqr-navy]'
                          : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border-2 border-[--clinqr-border]/10 hover:bg-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <span className="block text-xs text-[--clinqr-text-secondary]">Corner Squares</span>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Corner squares style">
                    {CORNER_SQUARE_STYLE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={(watchedQrStyle.corners_square_type || 'square') === opt.value}
                        onClick={() => form.setValue('qr_style.corners_square_type', opt.value, { shouldDirty: true })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          (watchedQrStyle.corners_square_type || 'square') === opt.value
                            ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border-2 border-[--clinqr-navy]'
                            : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border-2 border-[--clinqr-border]/10 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <span className="block text-xs text-[--clinqr-text-secondary]">Corner Dots</span>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Corner dots style">
                    {CORNER_DOT_STYLE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={(watchedQrStyle.corners_dot_type || 'square') === opt.value}
                        onClick={() => form.setValue('qr_style.corners_dot_type', opt.value, { shouldDirty: true })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          (watchedQrStyle.corners_dot_type || 'square') === opt.value
                            ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border-2 border-[--clinqr-navy]'
                            : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border-2 border-[--clinqr-border]/10 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================== Smart Routing ============================== */}
        <section id="smart-routing" ref={setSection('smart-routing')} className="space-y-4 scroll-mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[--clinqr-text-primary]">Smart Routing</h2>
            <div className="flex items-center gap-2">
              {routingRules.rules.length > 0 && (
                <span className="text-xs bg-[--clinqr-lime]/20 text-[--clinqr-navy] px-2 py-1 rounded-full font-medium">
                  {routingRules.rules.length} rule{routingRules.rules.length > 1 ? 's' : ''}
                </span>
              )}
              <SectionInfoButton onClick={() => setShowRulesInfoModal(true)} title="How do rules work?" />
            </div>
          </div>
          <RuleBuilder
            rules={routingRules}
            onChange={(rules) => {
              setRoutingRules(rules);
              form.setValue('routing_rules', rules as any, { shouldDirty: true });
            }}
            fallbackUrl={watchedFallbackUrl || initialData.fallback_url || ''}
            defaultExpanded={false}
            hasGeoRoutingFeature={user?.subscription?.plan?.features?.geographic_routing ?? false}
          />
        </section>

        {/* ============================== Security ============================== */}
        <section id="security" ref={setSection('security')} className="space-y-4 scroll-mt-4">
          <h2 className="text-lg font-semibold text-[--clinqr-text-primary]">Security</h2>

          <div>
            <FieldLabel htmlFor="expires_at" info="After this date, scanning the QR code shows an expired message instead of redirecting.">
              Expiration Date
            </FieldLabel>
            <Input
              id="expires_at"
              type="datetime-local"
              {...form.register('expires_at')}
              error={form.formState.errors.expires_at?.message}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <span className="block text-sm font-medium text-[--clinqr-text-primary]">Password Protection</span>
              <FieldInfoTooltip text="Scanners must enter this password before being redirected to the destination." />
            </div>

            {pendingPasswordRemoval ? (
              <div className="flex items-center justify-between p-3 border border-amber-200 rounded-lg bg-amber-50">
                <p className="text-sm text-amber-800">Password protection will be removed when you save.</p>
                <button
                  type="button"
                  onClick={() => setPendingPasswordRemoval(false)}
                  className="text-xs font-medium text-amber-700 hover:text-amber-900 px-2 py-1 rounded hover:bg-amber-100"
                >
                  Undo
                </button>
              </div>
            ) : confirmingRemoval ? (
              <div className="flex items-center justify-between p-3 border border-red-200 rounded-lg bg-red-50">
                <p className="text-sm text-red-800">Remove password protection when you save?</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmingRemoval(false)}
                    className="text-xs font-medium text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary] px-2 py-1 rounded hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPendingPasswordRemoval(true);
                      setConfirmingRemoval(false);
                    }}
                    className="text-xs font-medium text-red-700 hover:text-red-900 px-2 py-1 rounded hover:bg-red-100"
                  >
                    Confirm removal
                  </button>
                </div>
              </div>
            ) : passwordProtected && !showPasswordField ? (
              <div className="flex items-center justify-between p-3 border border-[--clinqr-border] rounded-lg bg-[--clinqr-bg-subtle]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[--clinqr-text-primary]">Password Protected</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordField(true)}
                    className="text-xs text-[--clinqr-primary] hover:text-[--clinqr-primary-dark] font-medium px-2 py-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    Change
                  </button>
                  <div className="w-px h-3 bg-gray-300" />
                  <button
                    type="button"
                    onClick={() => setConfirmingRemoval(true)}
                    className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <Input
                  type="password"
                  placeholder={passwordProtected ? 'Enter new password' : 'Leave empty for no password'}
                  autoComplete="new-password"
                  {...form.register('password')}
                  error={form.formState.errors.password?.message}
                />
                {passwordProtected && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordField(false);
                      form.setValue('password', '');
                    }}
                    className="text-xs text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary]"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ============================== UTM Parameters ============================== */}
        <section id="utm" ref={setSection('utm')} className="space-y-4 scroll-mt-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-[--clinqr-text-primary]">UTM Parameters</h2>
            <SectionInfoButton onClick={() => setShowUtmInfoModal(true)} title="What are UTM parameters?" />
          </div>
          <p className="text-xs text-[--clinqr-text-secondary]">
            Appended to the destination link when this QR code is scanned, so campaign traffic shows up in your
            analytics tools (Google Analytics, etc). Leave blank to not tag this QR code.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Source (optional)" placeholder="e.g. print, poster" {...form.register('utm_parameters.utm_source')} />
            <Input label="Medium (optional)" placeholder="e.g. qr-code" {...form.register('utm_parameters.utm_medium')} />
            <Input label="Campaign (optional)" placeholder="e.g. summer-sale" {...form.register('utm_parameters.utm_campaign')} />
            <Input label="Term (optional)" placeholder="e.g. lp-a" {...form.register('utm_parameters.utm_term')} />
            <Input label="Content (optional)" placeholder="e.g. header-banner" {...form.register('utm_parameters.utm_content')} />
          </div>
        </section>

        {/* ============================== Advanced ============================== */}
        <section id="advanced" ref={setSection('advanced')} className="space-y-4 scroll-mt-4">
          <h2 className="text-lg font-semibold text-[--clinqr-text-primary]">Advanced</h2>

          <FieldLabel htmlFor="max_scans" info="Once this many scans are reached, the QR code stops redirecting until you raise or remove the limit.">
            Max Scans
          </FieldLabel>
          <Input
            id="max_scans"
            type="number"
            placeholder="Unlimited"
            {...form.register('max_scans', { valueAsNumber: true })}
            error={form.formState.errors.max_scans?.message ? String(form.formState.errors.max_scans.message) : undefined}
          />

          <div>
            <span className="block text-sm font-medium text-[--clinqr-text-primary] mb-2">Status</span>
            <div className="flex items-center justify-between gap-4 p-4 border border-[--clinqr-border] rounded-lg bg-[--clinqr-bg-subtle]">
              <div className="flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${watchedIsActive ? 'bg-green-500' : 'bg-red-500'}`} />
                <div>
                  <p className="text-sm font-medium text-[--clinqr-text-primary]">
                    {watchedIsActive ? 'Active' : 'Paused'}
                  </p>
                  <p className="text-xs text-[--clinqr-text-secondary] mt-0.5">
                    {watchedIsActive
                      ? 'This QR code redirects scanners to its destination.'
                      : 'Scanning this QR code will not redirect anywhere until reactivated.'}
                  </p>
                </div>
              </div>
              <div className="inline-flex rounded-lg border-2 border-[--clinqr-border] overflow-hidden flex-shrink-0" role="group" aria-label="QR code status">
                <button
                  type="button"
                  aria-pressed={watchedIsActive}
                  onClick={() => form.setValue('is_active', true, { shouldDirty: true })}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    watchedIsActive ? 'bg-[--clinqr-navy] text-white' : 'bg-white text-[--clinqr-text-secondary] hover:bg-gray-50'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  aria-pressed={!watchedIsActive}
                  onClick={() => form.setValue('is_active', false, { shouldDirty: true })}
                  className={`px-3 py-1.5 text-xs font-semibold border-l-2 border-[--clinqr-border] transition-colors ${
                    !watchedIsActive ? 'bg-red-600 text-white' : 'bg-white text-[--clinqr-text-secondary] hover:bg-gray-50'
                  }`}
                >
                  Paused
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* How Rules Work Info Modal */}
      <Dialog open={showRulesInfoModal} onOpenChange={setShowRulesInfoModal}>
        <DialogContent className="bg-white border border-[--clinqr-border] rounded-lg shadow-lg max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[--clinqr-navy] flex items-center gap-2 font-serif">
              How Smart Routing Works
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-[--clinqr-text-secondary]">
            <p>Smart routing lets you automatically send users to different URLs based on conditions like time, location, or device.</p>
            <p>Rules are checked in order. The first matching rule wins. If no rules match, users go to the default URL.</p>
          </div>
          <DialogFooter>
            <Button
              variant="primary"
              onClick={() => setShowRulesInfoModal(false)}
              className="w-full !bg-[--clinqr-lime] !text-[--clinqr-navy] hover:!bg-[--clinqr-lime-bright]"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* What Are UTM Parameters Info Modal */}
      <Dialog open={showUtmInfoModal} onOpenChange={setShowUtmInfoModal}>
        <DialogContent className="bg-white border border-[--clinqr-border] rounded-lg shadow-lg max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[--clinqr-navy] flex items-center gap-2 font-serif">
              What Are UTM Parameters
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-[--clinqr-text-secondary]">
            <p>UTM parameters are small tags added to the end of your destination URL that tell analytics tools like Google Analytics where a visit came from.</p>
            <p><span className="font-medium text-[--clinqr-text-primary]">Source</span> is where the traffic came from (e.g. "poster"), <span className="font-medium text-[--clinqr-text-primary]">Medium</span> is the channel type (e.g. "qr-code"), and <span className="font-medium text-[--clinqr-text-primary]">Campaign</span> names the specific promotion. Term and Content are optional, finer-grained tags.</p>
            <p>Leave all fields blank to send scanners to the destination URL unmodified.</p>
          </div>
          <DialogFooter>
            <Button
              variant="primary"
              onClick={() => setShowUtmInfoModal(false)}
              className="w-full !bg-[--clinqr-lime] !text-[--clinqr-navy] hover:!bg-[--clinqr-lime-bright]"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showLogoPicker && (
        <ImagePickerModal
          onSelect={(image: ImageItem) => {
            form.setValue('logo_image_id', image.id, { shouldDirty: true });
            form.setValue('custom_logo_url', '', { shouldDirty: true });
            setLogoImage({ id: image.id, url: image.url, filename: image.filename });
            setShowLogoPicker(false);
          }}
          onClose={() => setShowLogoPicker(false)}
        />
      )}

      {showFallbackImagePicker && (
        <ImagePickerModal onSelect={selectFallbackImage} onClose={() => setShowFallbackImagePicker(false)} />
      )}
    </form>
  );
}
